import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { isAxiosError } from 'helpers/clientHelpers';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useContext } from 'react';
import { CANDIDATE_STATUS } from 'services/candidates';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { QueryKey } from 'types/query';
import { useCandidateAction, WrappedHookParams } from './useCandidateAction';

export const useShortlist = (params: WrappedHookParams) => {
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);

  return useCandidateAction<ShortlistHookCallbackParams>(
    async ({ candidate, campaignId }) => {
      await campaignsClient.createOrUpdateCampaignCandidate({
        campaignId: campaignId ?? candidate.campaignId,
        esPersonId: candidate.es_person_id,
        status: CANDIDATE_STATUS.SHORTLISTED,
        is_unlocked: candidate.isUnlocked,
      });
    },
    {
      onError: () => {
        return async ({ candidateOrchestrator: co, data: error }) => {
          if (isAxiosError(error) && error.response?.status === 409) {
            createSnackbar(dictionary.candidateAlreadyLinkedToAnotherMatch, {
              variant: 'danger',
            });

            co.campaign.decreaseStatOf.currentJobBoard();
            co.campaign.decreaseStatOf.currentList();
            co.campaign.remove.fromCurrentList();
          }
          throw error;
        };
      },
      onSuccess: ({ fcParams: { candidate, campaignId }, gtm }) => {
        gtm.shortlistCandidate(campaignId ?? candidate.campaignId, candidate.es_person_id);
        createSnackbar(dictionary.candidateIsMovedTo(candidate.firstName, dictionary.shortlisted), {
          variant: 'success',
        });
        queryClient.invalidateQueries(
          [QueryKey.useCandidateActivityLogQuery, { candidateEsPersonId: candidate.es_person_id }],
          { exact: false },
        );

        return async ({ candidateOrchestrator: co, pageContext }) => {
          if (!campaignId) {
            if (pageContext === PageContext.Campaign) {
              if (candidate.status === CANDIDATE_STATUS.NEW) {
                co.campaign.decreaseStatOf.currentJobBoard();
              }

              co.campaign.remove.fromCurrentList();
              co.campaign.reset.shortlistList();
              co.campaign.decreaseStatOf.currentList();
              co.campaign.increaseStatOf.shortlistList();
            } else if (pageContext === PageContext.RatedCandidates) {
              co.ratedCandidates.remove.fromCurrentList();
              co.ratedCandidates.reset.shortlistList();
              co.ratedCandidates.decreaseStatOf.currentList();
              co.ratedCandidates.increaseStatOf.shortlistList();
            } else if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
              co.share.moveCandidateTo.shortlistList();
            }
          }
        };
      },
    },
  )(params);
};

type ShortlistHookCallbackParams = {
  campaignId?: number;
};
